/**
 * Download the distribution file for a report, in the format selected in the
 * distribution dialog.
 *
 * The three preview endpoints render exactly what would be transmitted over
 * Medical Objects — including the addressee line, which is why the chosen
 * recipient is passed through — so a download reuses them rather than adding a
 * second rendering path. Two renderers for one document would eventually disagree, and
 * a file that differs from what the practice actually received is worse than no
 * download at all.
 *
 * Why this is a plain function and not a Redux thunk: a file save is an action,
 * not state. Nothing about the resulting blob belongs in the store, and putting
 * it there would keep a whole PDF in memory for the life of the session.
 *
 * Why the file is fetched rather than opened via a link: these endpoints require
 * the bearer token, which lives in a header, not a cookie. A plain anchor or a
 * new-window open sends no Authorization header and gets a 401.
 */

import { getToken } from './authUtils';
import { hl7BaseURL } from '../constant';

export type ReportFileFormat = 'pdf' | 'txt' | 'rtf';

const ENDPOINT: Record<ReportFileFormat, string> = {
  pdf: 'preview-pdf',
  txt: 'preview-txt',
  rtf: 'preview-rtf',
};

const EXTENSION: Record<ReportFileFormat, string> = {
  pdf: 'pdf',
  txt: 'txt',
  rtf: 'rtf',
};

/**
 * Build a filename that is safe on Windows, macOS and Linux.
 *
 * Practice staff file these by hand, so the patient name has to survive into the
 * name — but `/ \ : * ? " < > |` are all illegal somewhere, and a leading dot
 * hides the file on Unix. Falls back to "report" rather than producing an
 * extension-only name like ".pdf".
 */
function safeFileName(parts: Array<string | undefined>, extension: string): string {
  const stem = parts
    .filter(part => part && part.trim())
    .join(' - ')
    .replace(/[/\\:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/^[.\s-]+|[.\s-]+$/g, '')
    .slice(0, 120);
  return `${stem || 'report'}.${extension}`;
}

/**
 * The chosen recipient, so the download shows the same addressee line the practice
 * will see. Omitted → the document renders without one, exactly as it would if sent
 * with no recipient. A placeholder here would be a preview that lies, which is the
 * thing this file exists to avoid.
 */
export type DownloadRecipient = {
  id?: string;
  firstName?: string;
  lastName?: string;
  prefix?: string;
  idType?: string;
};

export type DownloadReportFileArgs = {
  reportId: string;
  format: ReportFileFormat;
  /** Used to build the filename. Optional — a generic name is used without it. */
  patientName?: string;
  reportName?: string;
  recipient?: DownloadRecipient | null;
};

/** Same param names the four preview endpoints share. */
function recipientQuery(recipient?: DownloadRecipient | null): string {
  if (!recipient?.id) return '';
  const params = new URLSearchParams({ toProviderId: recipient.id });
  if (recipient.lastName) params.set('toLastName', recipient.lastName);
  if (recipient.firstName) params.set('toFirstName', recipient.firstName);
  if (recipient.prefix) params.set('toPrefix', recipient.prefix);
  if (recipient.idType) params.set('toIdType', recipient.idType);
  return `?${params.toString()}`;
}

export type DownloadReportFileResult = { ok: true } | { ok: false; error: string };

export async function downloadReportFile({
  reportId,
  format,
  patientName,
  reportName,
  recipient,
}: DownloadReportFileArgs): Promise<DownloadReportFileResult> {
  if (!reportId) {
    return { ok: false, error: 'No report selected.' };
  }

  const token = getToken();
  if (!token) {
    return { ok: false, error: 'Your session has expired. Please sign in again.' };
  }

  let response: Response;
  try {
    response = await fetch(
      `${hl7BaseURL}/hl7/${ENDPOINT[format]}/${encodeURIComponent(reportId)}${recipientQuery(recipient)}`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  } catch {
    return { ok: false, error: 'Could not reach the server. Check your connection and try again.' };
  }

  // A refusal comes back as a JSON ApiResponse, not as a document. Saving the
  // response blindly would hand the user a .rtf file containing an error object,
  // which looks like a corrupt report rather than a refusal — so the body is
  // read as text and surfaced.
  if (!response.ok) {
    let message = `The server refused the request (${response.status}).`;
    try {
      const body = await response.text();
      const parsed = JSON.parse(body);
      if (parsed?.msg) {
        message = String(parsed.msg);
      }
    } catch {
      /* not JSON — keep the status-based message */
    }
    return { ok: false, error: message };
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    return { ok: false, error: 'The server returned an empty file.' };
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = safeFileName([patientName, reportName], EXTENSION[format]);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Revoking immediately can cancel the save in some browsers, so this is
    // deferred rather than done synchronously. Without it the blob leaks for the
    // life of the tab, which for a multi-megabyte PDF matters.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
  }

  return { ok: true };
}
